// src/components/ThemeZone.jsx
//
// Wrapper component that applies a zone's visual identity.
// Injects CSS custom properties, loads fonts dynamically.
//
// Usage:
//   <ThemeZone zone="dossier"><BountyDetail /></ThemeZone>
//   <ThemeZone><App /></ThemeZone>  // auto-detect from route

import { useEffect, useMemo } from 'react'
import { THEMES, FONT_IMPORTS, resolveZone } from '../lib/themeTokens'

const loadedFonts = new Set()

function loadFont(zone) {
  if (loadedFonts.has(zone)) return
  const url = FONT_IMPORTS[zone]
  if (!url) return
  const link = document.createElement('link')
  link.rel = 'stylesheet'
  link.href = url
  link.dataset.dcZone = zone
  document.head.appendChild(link)
  loadedFonts.add(zone)
}

export default function ThemeZone({ zone, children, className = '', style = {} }) {
  const resolvedZone = zone || resolveZone(window.location.pathname)
  const theme = THEMES[resolvedZone] || THEMES.recon

  useEffect(() => {
    loadFont(resolvedZone)
  }, [resolvedZone])

  const themeStyle = useMemo(() => {
    const vars = {}
    for (const [key, value] of Object.entries(theme)) {
      if (key.startsWith('--dc-')) {
        vars[key] = value
      }
    }
    return { ...vars, ...style }
  }, [theme, style])

  return (
    <div
      className={`dc-zone dc-zone-${resolvedZone} ${className}`}
      style={themeStyle}
      data-dc-zone={resolvedZone}
    >
      {children}
    </div>
  )
}